import React, { useState, useEffect } from 'react';
import { designerAPI, dashboardAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  X,
  Users,
  Mail,
  Briefcase,
  Hash,
  UserCheck,
  Calendar,
  MapPin,
  Filter,
  LayoutGrid,
  List,
  Building2,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ROLES } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'sonner';
import { cn } from '../lib/utils';

const VERTICALS = [
  'Slot Product',
  'Asia Hub',
  'LC Product',
  'EU Hub',
  'Asia Promotions',
  'SWAT Team',
  'Central Promotion',
  'Streaming Editor',
  'Central Marketing',
  'All'
];

const Designers = () => {
  const { isManager } = useAuth();
  const { isDark } = useTheme();
  const [designers, setDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [subTeamFilter, setSubTeamFilter] = useState('all');
  const [viewMode, setViewMode] = useState('thumbnail'); // 'thumbnail' or 'list'
  const [roleBreakdown, setRoleBreakdown] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDesigner, setEditingDesigner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    vertical_team: '',
    employee_id: '',
    direct_manager: '',
    date_of_joining: '',
    designer_type: '',
    location: '',
    sub_team: '',
  });

  const fetchDesigners = async () => {
    try {
      const [designersRes, breakdownRes] = await Promise.all([
        designerAPI.getAll(),
        dashboardAPI.getRoleBreakdown()
      ]);
      setDesigners(designersRes.data);
      setRoleBreakdown(breakdownRes.data);
    } catch (error) {
      console.error('Error fetching designers:', error);
      toast.error('Failed to load designers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDesigners();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDesigner) {
        await designerAPI.update(editingDesigner.id, formData);
        toast.success('Designer updated successfully');
      } else {
        await designerAPI.create(formData);
        toast.success('Designer added successfully');
      }
      setIsModalOpen(false);
      setEditingDesigner(null);
      setFormData({ name: '', email: '', role: '', vertical_team: '', employee_id: '', direct_manager: '', date_of_joining: '', designer_type: '', location: '' });
      fetchDesigners();
    } catch (error) {
      console.error('Error saving designer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save designer');
    }
  };

  const handleEdit = (designer) => {
    setEditingDesigner(designer);
    setFormData({
      name: designer.name,
      email: designer.email,
      role: designer.role,
      vertical_team: designer.vertical_team,
      employee_id: designer.employee_id || '',
      direct_manager: designer.direct_manager || '',
      date_of_joining: designer.date_of_joining || '',
      designer_type: designer.designer_type || '',
      location: designer.location || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this designer?')) return;
    
    try {
      await designerAPI.delete(id);
      toast.success('Designer deleted successfully');
      fetchDesigners();
    } catch (error) {
      console.error('Error deleting designer:', error);
      toast.error('Failed to delete designer');
    }
  };

  const filteredDesigners = designers.filter((d) => {
    const matchesSearch = 
      d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (d.vertical_team && d.vertical_team.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (d.sub_team && d.sub_team.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (d.employee_id && d.employee_id.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesLocation = locationFilter === 'all' || d.location === locationFilter;
    const matchesType = typeFilter === 'all' || d.designer_type === typeFilter;
    const matchesSubTeam = subTeamFilter === 'all' || d.sub_team === subTeamFilter;
    
    return matchesSearch && matchesLocation && matchesType && matchesSubTeam;
  });

  // Get unique values from data
  const locations = [...new Set(designers.map(d => d.location).filter(Boolean))];
  const designerTypes = [...new Set(designers.map(d => d.designer_type).filter(Boolean))];
  const subTeams = [...new Set(designers.map(d => d.sub_team).filter(Boolean))].sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="designers-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={cn("text-2xl md:text-3xl font-bold font-outfit", isDark ? "text-white" : "text-slate-800")}>
            Designers
          </h1>
          <p className={cn("mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            Manage your creative team members
          </p>
        </div>
        {isManager() && (
          <Button 
            onClick={() => {
              setEditingDesigner(null);
              setFormData({ name: '', email: '', role: '', vertical_team: '', employee_id: '', direct_manager: '', date_of_joining: '', designer_type: '', location: '', sub_team: '' });
              setIsModalOpen(true);
            }}
            className="bg-[#331062] hover:bg-[#2A0D52]"
            data-testid="add-designer-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Designer
          </Button>
        )}
      </div>

      {/* Team Breakdown Section */}
      {roleBreakdown && (
        <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
          <CardHeader className="pb-3">
            <CardTitle className={cn("text-lg font-outfit flex items-center gap-2", isDark ? "text-white" : "")}>
              <Building2 className="w-5 h-5 text-[#331062]" />
              Team Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {Object.entries(roleBreakdown.by_sub_team || {}).slice(0, 12).map(([subTeam, count]) => (
                <button
                  key={subTeam}
                  onClick={() => setSubTeamFilter(subTeamFilter === subTeam ? 'all' : subTeam)}
                  className={cn(
                    "p-3 rounded-xl text-center transition-all hover:scale-105",
                    subTeamFilter === subTeam
                      ? "bg-[#331062] text-white ring-2 ring-[#331062] ring-offset-2"
                      : isDark 
                        ? "bg-slate-700/50 hover:bg-slate-700" 
                        : "bg-slate-50 hover:bg-slate-100"
                  )}
                  data-testid={`filter-subteam-${subTeam.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <p className={cn(
                    "text-xl font-bold",
                    subTeamFilter === subTeam ? "text-white" : isDark ? "text-white" : "text-slate-800"
                  )}>
                    {count}
                  </p>
                  <p className={cn(
                    "text-xs font-medium truncate",
                    subTeamFilter === subTeam ? "text-white/80" : isDark ? "text-slate-400" : "text-slate-500"
                  )} title={subTeam}>
                    {subTeam}
                  </p>
                </button>
              ))}
            </div>
            <div className={cn(
              "mt-3 pt-3 border-t flex items-center justify-between",
              isDark ? "border-slate-700" : "border-slate-200"
            )}>
              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                Total: <span className="font-semibold text-[#331062] dark:text-[#CCFF00]">{roleBreakdown.total}</span> designers
              </p>
              {subTeamFilter !== 'all' && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSubTeamFilter('all')}
                  className="text-[#331062] hover:text-[#4a1d8a]"
                >
                  Clear filter
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filters */}
      <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name, email, ID, role, sub-team..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn("pl-10", isDark ? "bg-slate-700 border-slate-600" : "")}
                data-testid="search-input"
              />
            </div>
            
            {/* Sub Team Filter */}
            <div className="w-full md:w-48">
              <Select value={subTeamFilter} onValueChange={setSubTeamFilter}>
                <SelectTrigger data-testid="subteam-filter" className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                  <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Sub Teams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sub Teams</SelectItem>
                  {subTeams.map(team => (
                    <SelectItem key={team} value={team}>{team}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Location Filter */}
            <div className="w-full md:w-40">
              <Select value={locationFilter} onValueChange={setLocationFilter}>
                <SelectTrigger data-testid="location-filter" className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                  <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Type Filter */}
            <div className="w-full md:w-36">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="type-filter" className={isDark ? "bg-slate-700 border-slate-600" : ""}>
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {designerTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Filter Summary & View Toggle */}
          <div className="flex items-center justify-between mt-3">
            <div className={cn("flex items-center gap-4 text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
              <span>Showing {filteredDesigners.length} of {designers.length} designers</span>
              {(locationFilter !== 'all' || typeFilter !== 'all' || subTeamFilter !== 'all') && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setLocationFilter('all'); setTypeFilter('all'); setSubTeamFilter('all'); }}
                  className="text-[#331062] hover:text-[#4a1d8a]"
                >
                  Clear filters
                </Button>
              )}
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <Button
                variant={viewMode === 'thumbnail' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('thumbnail')}
                className={viewMode === 'thumbnail' ? 'bg-[#331062] text-white' : 'text-slate-600'}
                data-testid="view-thumbnail"
              >
                <LayoutGrid className="w-4 h-4 mr-1" />
                Thumbnail
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-[#331062] text-white' : 'text-slate-600'}
                data-testid="view-list"
              >
                <List className="w-4 h-4 mr-1" />
                List
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Thumbnail View */}
      {viewMode === 'thumbnail' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredDesigners.map((designer, index) => (
              <motion.div
                key={designer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="card-hover" data-testid={`designer-card-${designer.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#331062]/10 flex items-center justify-center">
                          <span className="text-[#331062] font-bold text-lg font-outfit">
                            {designer.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{designer.name}</h3>
                          <p className="text-sm text-slate-500">{designer.role}</p>
                        </div>
                      </div>
                      {isManager() && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(designer)}
                            data-testid={`edit-designer-${designer.id}`}
                          >
                            <Edit2 className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(designer.id)}
                            data-testid={`delete-designer-${designer.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="mt-4 space-y-2">
                      {designer.employee_id && (
                        <div className={cn("flex items-center gap-2 text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                          <Hash className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">ID:</span> {designer.employee_id}
                        </div>
                      )}
                      <div className={cn("flex items-center gap-2 text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                        <Mail className="w-4 h-4 text-slate-400" />
                        {designer.email}
                      </div>
                      {designer.sub_team && (
                        <div className={cn("flex items-center gap-2 text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">Sub Team:</span>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#331062]/10 text-[#331062] dark:bg-[#CCFF00]/10 dark:text-[#CCFF00]">
                            {designer.sub_team}
                          </span>
                        </div>
                      )}
                      {designer.designer_type && (
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            designer.designer_type.toLowerCase() === 'motion' 
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' 
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                          }`}>
                            {designer.designer_type}
                          </span>
                          {designer.location && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              designer.location === 'Noida' 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' 
                                : 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                            }`}>
                              {designer.location}
                            </span>
                          )}
                        </div>
                      )}
                      {designer.direct_manager && (
                        <div className={cn("flex items-center gap-2 text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                          <UserCheck className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">Manager:</span> {designer.direct_manager || designer.manager}
                        </div>
                      )}
                      {designer.date_of_joining && (
                        <div className={cn("flex items-center gap-2 text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">Joined:</span> {designer.date_of_joining}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={cn("border-b", isDark ? "bg-slate-700/50 border-slate-600" : "bg-slate-50")}>
                  <tr className={cn("text-left text-xs uppercase tracking-wider", isDark ? "text-slate-400" : "text-slate-500")}>
                    <th className="px-4 py-3 font-medium">Employee</th>
                    <th className="px-4 py-3 font-medium">ID</th>
                    <th className="px-4 py-3 font-medium">Role</th>
                    <th className="px-4 py-3 font-medium">Sub Team</th>
                    <th className="px-4 py-3 font-medium">Location</th>
                    <th className="px-4 py-3 font-medium">Manager</th>
                    {isManager() && <th className="px-4 py-3 font-medium text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className={cn("divide-y", isDark ? "divide-slate-700" : "divide-slate-100")}>
                  {filteredDesigners.map((designer, index) => (
                    <motion.tr
                      key={designer.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className={cn("transition-colors", isDark ? "hover:bg-slate-700/50" : "hover:bg-slate-50")}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#331062]/10 flex items-center justify-center">
                            <span className="text-[#331062] font-bold text-sm">
                              {designer.name.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>{designer.name}</p>
                            <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>{designer.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className={cn("px-4 py-3 text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                        {designer.employee_id || '-'}
                      </td>
                      <td className={cn("px-4 py-3 text-sm max-w-[200px] truncate", isDark ? "text-slate-300" : "text-slate-600")}>
                        {designer.role}
                      </td>
                      <td className="px-4 py-3">
                        {designer.sub_team && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#331062]/10 text-[#331062] dark:bg-[#CCFF00]/10 dark:text-[#CCFF00]">
                            {designer.sub_team}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {designer.location && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            designer.location === 'Noida' 
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' 
                              : 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300'
                          }`}>
                            {designer.location}
                          </span>
                        )}
                      </td>
                      <td className={cn("px-4 py-3 text-sm max-w-[180px] truncate", isDark ? "text-slate-300" : "text-slate-600")}>
                        {designer.direct_manager || designer.manager || '-'}
                      </td>
                      {isManager() && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(designer)}
                            >
                              <Edit2 className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(designer.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {filteredDesigners.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-800">No designers found</h3>
          <p className="text-slate-500 mt-1">
            {searchQuery ? 'Try a different search term' : 'Add your first team member'}
          </p>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editingDesigner ? 'Edit Designer' : 'Add Designer'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    required
                    data-testid="designer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employee_id">Employee ID</Label>
                  <Input
                    id="employee_id"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    placeholder="EMP001"
                    data-testid="designer-employee-id-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@gmail.com"
                  required
                  data-testid="designer-email-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="designer-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((role) => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vertical">Vertical / Team *</Label>
                  <Select
                    value={formData.vertical_team}
                    onValueChange={(value) => setFormData({ ...formData, vertical_team: value })}
                  >
                    <SelectTrigger data-testid="designer-vertical-select">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      {VERTICALS.map((vertical) => (
                        <SelectItem key={vertical} value={vertical}>{vertical}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="direct_manager">Direct Manager</Label>
                  <Input
                    id="direct_manager"
                    value={formData.direct_manager}
                    onChange={(e) => setFormData({ ...formData, direct_manager: e.target.value })}
                    placeholder="Manager Name"
                    data-testid="designer-manager-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_of_joining">Date of Joining</Label>
                  <Input
                    id="date_of_joining"
                    type="date"
                    value={formData.date_of_joining}
                    onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })}
                    data-testid="designer-doj-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="designer_type">Designer Type</Label>
                  <Select
                    value={formData.designer_type}
                    onValueChange={(value) => setFormData({ ...formData, designer_type: value })}
                  >
                    <SelectTrigger data-testid="designer-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Static">Static</SelectItem>
                      <SelectItem value="Motion">Motion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Noida, Kolkata"
                    data-testid="designer-location-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#331062] hover:bg-[#2A0D52]"
                data-testid="designer-submit-btn"
              >
                {editingDesigner ? 'Update' : 'Add'} Designer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Designers;
